import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import { classNames } from "@/helpers/classNames";

import MarkdownWrapper from "@/components/markdownWrapper";
import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";

import { ChevronDoubleRightIcon, XMarkIcon } from "@heroicons/react/24/outline";

/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/typography'),
    ],
  }
  ```
*/

export const query = graphql`
  fragment KnowledgeBaseContentWithSidebarIssue on StrapiIssueTemplate {
    url
    title
    category {
      name
    }
    tags {
      name
    }
    selectedText {
      text
      additionalText
    }
    references {
      title
      url
    }
  }

  fragment KnowledgeBaseContentWithSidebarIssueTag on StrapiIssueTag {
    name
  }

  fragment KnowledgeBaseContentWithSidebarIssueCategory on StrapiIssueCategory {
    name
  }

  fragment KnowledgeBaseContentWithSidebarData on StrapiComponentKnowledgeBaseContentWithSidebar {
    titleReferences
    sidebar {
      textOpenSidebar
      textCloseSidebar
      items {
        selected
        link {
          ...StrapiSharedLink
        }
      }
      highlightedTags {
        name
      }
    }
  }
`;

function ContentWithSidebar({ data, issues, allIssues = [] }) {
  if (!data || !issues?.length) {
    return <></>;
  }
  const issueData = issues[0];

  const highlightedTags = data.sidebar?.highlightedTags?.map(
    ({ name: tag }) => ({
      tag,
      issues: allIssues
        .filter(({ title, url }) => title && url)
        .filter(({ tags }) => tags?.find(({ name }) => name === tag))
        .sort(({ title: a }, { title: b }) =>
          a.localeCompare(b, issueData?.locale || "US-en", {
            numeric: true,
            sensitivity: "base",
          })
        ),
    })
  );

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 top-20 bg-background-primary bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 top-20 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute right-0 top-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">
                        {data.sidebar?.textCloseSidebar || "Close sidebar"}
                      </span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-0 flex-1 overflow-y-auto pb-4 pt-5">
                  <nav className="mt-5 flex-1 bg-white px-2">
                    <div className="grid grid-cols-1 gap-1">
                      {Boolean(data.sidebar?.items?.length) &&
                        data.sidebar.items.map(
                          ({ selected, link }, index) =>
                            (link?.overwriteTitle || link?.link?.title) && (
                              <LinkWrapper
                                key={index}
                                to={link?.link?.url}
                                className={classNames(
                                  selected
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                  "group flex items-center rounded-md px-2 py-2 text-sm font-medium"
                                )}
                              >
                                {(link?.overwriteIcon || link?.link?.icon) && (
                                  <MediaWrapper
                                    media={
                                      link?.overwriteIcon || link?.link?.icon
                                    }
                                    divClassName={classNames(
                                      selected
                                        ? "text-gray-500"
                                        : "text-gray-400 group-hover:text-gray-500",
                                      "mr-3 h-6 w-6 flex-shrink-0"
                                    )}
                                    aria-hidden="true"
                                  />
                                )}
                                {link?.overwriteTitle || link?.link?.title}
                              </LinkWrapper>
                            )
                        )}
                    </div>
                    {Boolean(highlightedTags?.length) &&
                      highlightedTags.map(
                        ({ tag, issues }, tagIndex) =>
                          Boolean(issues?.length) && (
                            <div key={tagIndex} className="mt-8 flex flex-col">
                              <span className="px-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {tag}
                              </span>
                              <div className="mt-2 grid grid-cols-1 gap-1">
                                {issues.map((issue, issueIndex) => (
                                  <LinkWrapper
                                    key={issueIndex}
                                    to={issue?.url}
                                    className="group flex items-center rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                  >
                                    {issue?.title}
                                  </LinkWrapper>
                                ))}
                              </div>
                            </div>
                          )
                      )}
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="relative mx-auto my-4 flex max-w-7xl grow flex-col px-4 sm:px-6 md:my-8">
        <div className="hidden md:absolute md:bottom-0 md:top-0 md:flex md:w-64 md:flex-col">
          <div className="flex min-h-0 flex-1 flex-col bg-white">
            <div className="flex flex-1 flex-col overflow-y-auto py-1">
              <nav className="flex-1 bg-white pr-1">
                <div className="grid grid-cols-1 gap-1">
                  {Boolean(data.sidebar?.items?.length) &&
                    data.sidebar.items.map(
                      ({ selected, link }, index) =>
                        (link?.overwriteTitle || link?.link?.title) && (
                          <LinkWrapper
                            key={index}
                            to={link?.link?.url}
                            className={classNames(
                              selected
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                              "group flex items-center rounded-md px-2 py-2 text-sm font-medium"
                            )}
                          >
                            {(link?.overwriteIcon || link?.link?.icon) && (
                              <MediaWrapper
                                media={link?.overwriteIcon || link?.link?.icon}
                                divClassName={classNames(
                                  selected
                                    ? "text-gray-500"
                                    : "text-gray-400 group-hover:text-gray-500",
                                  "mr-3 h-6 w-6 flex-shrink-0"
                                )}
                                aria-hidden="true"
                              />
                            )}
                            {link?.overwriteTitle || link?.link?.title}
                          </LinkWrapper>
                        )
                    )}
                </div>
                {Boolean(highlightedTags?.length) &&
                  highlightedTags.map(
                    ({ tag, issues }, tagIndex) =>
                      Boolean(issues?.length) && (
                        <div key={tagIndex} className="mt-8 flex flex-col">
                          <span className="px-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                            {tag}
                          </span>
                          <div className="mt-2 grid grid-cols-1 gap-1">
                            {issues.map((issue, issueIndex) => (
                              <LinkWrapper
                                key={issueIndex}
                                to={issue?.url}
                                className="group flex items-center rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                              >
                                {issue?.title}
                              </LinkWrapper>
                            ))}
                          </div>
                        </div>
                      )
                  )}
              </nav>
            </div>
          </div>
        </div>
        <div className="flex grow flex-col md:pl-[18rem]">
          <div className="sticky top-0 z-10 bg-white pl-1 pt-1 sm:pl-3 sm:pt-3 md:hidden">
            <button
              type="button"
              className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-900"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">
                {data.sidebar?.textOpenSidebar || "Open sidebar"}
              </span>
              <ChevronDoubleRightIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <main className="flex grow flex-col">
            <div className="overflow-hidden bg-background-transparent">
              <div className="mx-auto w-full bg-background-white">
                <div className="border-b border-border-light-gray pb-3">
                  <div className="mt-2 flex">
                    {/* <div>
                      <span className=" mr-2 rounded-lg bg-[#00ffff] py-1 px-2 text-3xl font-normal font-normal leading-10 text-text-black">
                        9.8
                      </span>
                    </div> */}
                    {issueData.title && (
                      <h1 className="inline-block text-3xl font-normal leading-10 text-gray-500">
                        {issueData?.category?.name && (
                          <span>{`${issueData.category.name}`}</span>
                        )}
                        <span> / </span>
                        <span className="font-extrabold text-text-primary">
                          {issueData.title}
                        </span>
                      </h1>
                    )}
                  </div>
                  {issueData.tags?.length && (
                    <div className="mt-6 flex flex-wrap gap-4">
                      {issueData.tags.map(({ name: tag }, index) => (
                        <span
                          key={index}
                          className="rounded-full bg-gray-100 px-2 py-1 text-sm font-medium leading-5 text-gray-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="prose-prose-primary prose prose-lg mx-auto max-w-5xl text-base font-normal leading-7 text-text-secondary prose-headings:my-3 prose-headings:text-xl prose-headings:font-semibold prose-headings:leading-8 prose-headings:text-text-primary">
                  {Boolean(issueData.selectedText?.text?.length) && (
                    <MarkdownWrapper
                      text={issueData.selectedText.text}
                      replaceParagraph={false}
                      replaceComponents={{ h1: "h2" }}
                    />
                  )}
                  {Boolean(issueData.selectedText?.additionalText?.length) && (
                    <MarkdownWrapper
                      text={issueData.selectedText.additionalText}
                      replaceParagraph={false}
                      replaceComponents={{ h1: "h2" }}
                    />
                  )}
                  {Boolean(issueData.references?.length) &&
                    data.titleReferences && (
                      <div className="">
                        <h2>{data.titleReferences}</h2>
                        <ul>
                          {issueData.references.map(
                            (link, index) =>
                              (link.title || link.url) && (
                                <li key={index}>
                                  <LinkWrapper to={link.url}>
                                    {link.title || link.url}
                                  </LinkWrapper>
                                </li>
                              )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

ContentWithSidebar.propTypes = {
  data: PropTypes.object,
  issueData: PropTypes.object,
};

export default ContentWithSidebar;
